import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import api from '../api';
import { requestPushPermission } from '../firebase';
import {
  Bell, BellOff, Megaphone, CheckCheck, Trash2, Check, ClipboardList, Zap, UserX, LogOut, KeyRound,
  AlertTriangle, Play, Hand, Mail, CheckCircle, X, CloudLightning, ChevronRight, ChevronDown, RotateCcw, Pin,
} from 'lucide-react';
import TaskDetailModal from '../components/TaskDetailModal';
import SentNotifications from '../components/SentNotifications';
import { Seg } from '../components/AdminUi';

// Obavijesti nemaju vrstu u bazi — razvrstavamo po početku naslova (tekstovi iz app/src/index.js).
// cat = filter („Za mene”, „Prijave”, „Statusi”, „Ostalo”), hue = prigušena paleta kartica zadataka.
const KINDS = [
  { re: /^dodijeljen/i,                       Icon: Mail,          hue: '#3a6ea3', cat: 'me' },
  { re: /^obavezno sudjelovanje/i,            Icon: AlertTriangle, hue: '#ac463b', cat: 'me' },
  { re: /^status promijenjen/i,               Icon: Play,          hue: '#3a6ea3', cat: 'me' },
  { re: /^(uklonjen|poziv otkazan|prijava nije prihvaćena|zahtjev odbijen)/i, Icon: UserX, hue: '#ac463b', cat: 'me' },
  { re: /^zahtjev prihvaćen/i,                Icon: CheckCircle,   hue: '#3f8f6b', cat: 'me' },
  { re: /^prijedlog zadatka/i,                Icon: ClipboardList, hue: '#5a5f86', cat: 'me' },
  { re: /^nova prijava/i,                     Icon: Hand,          hue: '#a8781f', cat: 'vol' },
  { re: /zahtjev za odjavu/i,                 Icon: LogOut,        hue: '#a8781f', cat: 'vol' },
  { re: /^novi prijedlog/i,                   Icon: ClipboardList, hue: '#5a5f86', cat: 'vol' },
  { re: /^promjena statusa/i,                 Icon: Play,          hue: '#5b6472', cat: 'status' },
  { re: /neuspješan/i,                        Icon: X,             hue: '#ac463b', cat: 'status' },
  { re: /^zadatak (završen|zatvoren)/i,       Icon: Check,         hue: '#3f8f6b', cat: 'status' },
  { re: /^novi ponavljajući/i,                Icon: RotateCcw,     hue: '#5b6472', cat: 'status' },
  { re: /^nova intervencija/i,                Icon: Zap,           hue: '#ac463b', cat: 'other', target: 'interventions' },
  { re: /^novi (grupni )?zadatak/i,           Icon: ClipboardList, hue: '#1a2a4a', cat: 'other' },
  { re: /vremensko upozorenje/i,              Icon: CloudLightning,hue: '#c2621d', cat: 'other', target: 'home' },
  { re: /^poruka voditelja/i,                 Icon: Megaphone,     hue: '#1a2a4a', cat: 'me' },
  { re: /^oglasna ploča/i,                    Icon: Pin,           hue: '#1a2a4a', cat: 'other', target: 'board' },
  { re: /^obavijest/i,                        Icon: Megaphone,     hue: '#1a2a4a', cat: 'other', target: 'home' },
  { re: /lozinka/i,                           Icon: KeyRound,      hue: '#5a5f86', cat: 'other' },
];
const DEFAULT_KIND = { Icon: Bell, hue: '#5b6472', cat: 'other' };
// DHMZ upozorenje (ref_key „dhmz:…”): boja po razini iz naslova, „isteklo” nakon ref_until.
const isDhmz = (n) => (n.ref_key || '').startsWith('dhmz:');
const dhmzExpired = (n) => isDhmz(n) && n.ref_until && new Date(n.ref_until) < new Date();
const dhmzHue = (n) => (/· crveno$/.test(n.title) ? '#ac463b' : /· narančasto$/.test(n.title) ? '#c2621d' : '#a8781f');
const kindOf = (title) => KINDS.find(k => k.re.test((title || '').replace(/^\W+/, ''))) || DEFAULT_KIND;

// Stanje prijave / zahtjeva za odjavu (backend: req_state) — neovisno o „pročitano”.
const REQ_STATE = {
  pending:   { text: 'čeka odluku', color: '#a8781f' },
  accepted:  { text: 'prihvaćeno',  textRem: 'odobreno', color: '#3f8f6b' },
  declined:  { text: 'odbijeno',    color: '#ac463b' },
  withdrawn: { text: 'povučeno',    color: '#8a94a6' },
  closed:    { text: 'zadatak zatvoren', textInt: 'intervencija zatvorena', color: '#8a94a6' },
  removed:   { text: 'nije na intervenciji', color: '#8a94a6' },
};
const reqStateText = (n) => {
  const st = REQ_STATE[n.req_state];
  if (!st) return null;
  if (n.req_kind === 'rem' && st.textRem) return st.textRem;
  if (n.req_kind === 'int' && st.textInt) return st.textInt;
  return st.text;
};

const FILTERS = [
  { key: 'all', label: 'Sve' },
  { key: 'me', label: 'Za mene' },
  { key: 'vol', label: 'Prijave' },
  { key: 'status', label: 'Statusi' },
  { key: 'other', label: 'Ostalo' },
];

// „Promjena statusa: Pokazna vježba” → naslov retka = zadatak, a vrsta ide u podnaslov.
function splitTitle(title) {
  const clean = (title || '').replace(/^[^\p{L}\p{N}]+/u, '');
  const i = clean.indexOf(': ');
  if (i > 0) return { kind: clean.slice(0, i), main: clean.slice(i + 2) };
  return { kind: null, main: clean };
}

function dayKey(dateStr) {
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const diff = Math.round((t - d) / 86400000);
  if (diff === 0) return 'Danas';
  if (diff === 1) return 'Jučer';
  return d.toLocaleDateString('hr-HR', { day: 'numeric', month: 'long' });
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const mins = Math.floor((Date.now() - d) / 60000);
  if (mins < 1) return 'sada';
  if (mins < 60) return `${mins} min`;
  return d.toLocaleTimeString('hr-HR', { hour: '2-digit', minute: '2-digit' });   // dan je u naslovu skupine
}

const SWIPE_ACT = 90;      // px pomaka za brisanje / pročitano
const LONG_PRESS = 480;    // ms za ulazak u odabir
const UNDO_MS = 5000;

// Redak s gestama: udesno briše, ulijevo prebacuje pročitano ↔ nepročitano, dugi pritisak ulazi u odabir.
// Pointer capture tek nakon vodoravnog praga (vidi feedback_swipe_tap_gestures), tap ide kroz onClick.
function SwipeRow({ children, onTap, onSwipeRight, onSwipeLeft, onLongPress, disabled, isRead }) {
  const [dx, setDx] = useState(0);
  const [anim, setAnim] = useState(false);
  const st = useRef({ x: 0, y: 0, id: null, el: null, mode: null, moved: false, lp: null });

  const clearLp = () => { if (st.current.lp) { clearTimeout(st.current.lp); st.current.lp = null; } };

  const onDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    st.current = { x: e.clientX, y: e.clientY, id: e.pointerId, el: e.currentTarget, mode: null, moved: false, lp: null };
    if (onLongPress) {
      st.current.lp = setTimeout(() => {
        st.current.lp = null;
        st.current.moved = true;            // da onClick nakon pritiska ne otvori obavijest
        st.current.mode = 'lp';
        if (navigator.vibrate) { try { navigator.vibrate(12); } catch { /* ignore */ } }
        onLongPress();
      }, LONG_PRESS);
    }
  };
  const onMove = (e) => {
    const s = st.current;
    if (s.id !== e.pointerId || s.mode === 'lp') return;
    const mx = e.clientX - s.x, my = e.clientY - s.y;
    if (!s.mode) {
      if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
      clearLp();
      if (disabled || Math.abs(my) > Math.abs(mx)) { s.mode = 'scroll'; return; }
      s.mode = 'drag';
      s.moved = true;
      try { s.el.setPointerCapture(s.id); } catch { /* ignore */ }
    }
    if (s.mode !== 'drag') return;
    const v = mx;
    setAnim(false);
    setDx(v);
  };
  const onUp = () => {
    const s = st.current;
    clearLp();
    if (s.mode === 'drag') {
      try { s.el.releasePointerCapture(s.id); } catch { /* ignore */ }
      setAnim(true);
      if (dx > SWIPE_ACT) { setDx(window.innerWidth); setTimeout(() => onSwipeRight && onSwipeRight(), 180); return; }
      if (dx < -SWIPE_ACT) { setDx(0); onSwipeLeft && onSwipeLeft(); return; }
      setDx(0);
    }
  };
  const onClick = () => {
    if (st.current.moved) { st.current.moved = false; return; }
    onTap && onTap();
  };

  return (
    <div className="ntf-swipe">
      <div className="ntf-under" style={{ opacity: dx === 0 ? 0 : 1 }}>
        {dx > 0
          ? <span className="ntf-under-del" style={{ opacity: Math.min(1, dx / SWIPE_ACT) }}><Trash2 size={18} /> Obriši</span>
          : isRead
            ? <span className="ntf-under-read is-unread" style={{ opacity: Math.min(1, -dx / SWIPE_ACT) }}><Mail size={18} /> Nepročitano</span>
            : <span className="ntf-under-read" style={{ opacity: Math.min(1, -dx / SWIPE_ACT) }}><Check size={18} /> Pročitano</span>}
      </div>
      <div
        className="ntf-row-wrap"
        style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: anim ? 'transform .2s cubic-bezier(0.4,0,0.2,1)' : 'none' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onClick={onClick}
        onContextMenu={e => { if (onLongPress) e.preventDefault(); }}
      >
        {children}
      </div>
    </div>
  );
}

// Naslov + tekst obavijesti; ako ne stane (naslov u jedan red, tekst u dva), nudi „Prikaži više”.
function NotifText({ main, sub, expanded, onToggle }) {
  const titleRef = useRef(null);
  const subRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    if (expanded) return;   // prošireno: zadrži zadnje mjerenje, da gumb ostane
    const check = () => {
      const t = titleRef.current, sb = subRef.current;
      setOverflows(!!((t && t.scrollHeight > t.clientHeight + 1) || (sb && sb.scrollHeight > sb.clientHeight + 1)));
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [main, sub, expanded]);

  return (
    <span className={`ntf-tx${expanded ? ' is-open' : ''}`}>
      <b ref={titleRef}>{main}</b>
      {sub && <small ref={subRef}>{sub}</small>}
      {(overflows || expanded) && (
        <button
          type="button"
          className="ntf-expand"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); onToggle(); }}
        >
          {expanded ? 'Prikaži manje' : 'Prikaži više'}
          <ChevronDown size={15} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
        </button>
      )}
    </span>
  );
}

function Notifications({ onUnreadChange, user, navigate, onOpenDhmzWarning, filterRequest, onOpenIntervention, onOpenBoard }) {
  // „Poslane” vide samo oni koji mogu slati obavijesti: admin i voditelji grupa.
  const canSend = user?.role === 'admin' || !!user?.groups?.some(g => g.group_role === 'owner');
  const [box, setBox] = useState('in');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [hidden, setHidden] = useState(() => new Set());   // obrisano, čeka istek „Poništi”
  const [toast, setToast] = useState(null);                 // { text, undo }
  const [openTask, setOpenTask] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());   // obavijesti s prikazanim cijelim tekstom
  const toggleExpanded = (id) => setExpanded(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const pendingRef = useRef(null);                          // { ids, readAll, timer }

  // Otvaranje s filterom izvana (npr. Početna → „prijave čekaju odobrenje”); stranica ostaje mountana.
  useEffect(() => {
    if (filterRequest?.key) setFilter(filterRequest.key);
  }, [filterRequest]);

  useEffect(() => {
    fetchNotifications();
    fetchAnnouncement();
    checkPushStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Neizvršeno brisanje se izvrši i kad korisnik ode sa stranice.
  const commitPending = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    clearTimeout(p.timer);
    pendingRef.current = null;
    if (p.readAll) api.delete('/notifications/read').catch(() => {});
    else if (p.ids.length) api.post('/notifications/delete', { ids: p.ids }).catch(() => {});
  }, []);
  useEffect(() => () => commitPending(), [commitPending]);

  const reportUnread = (list, hide = hidden) => {
    onUnreadChange && onUnreadChange(list.filter(n => !n.is_read && !hide.has(n.id)).length);
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
      setHasMore(res.data.length >= 100);
      reportUnread(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadOlder = async () => {
    if (loadingMore || !notifications.length) return;
    setLoadingMore(true);
    try {
      const last = notifications[notifications.length - 1];
      const res = await api.get(`/notifications?before=${last.id}`);
      setNotifications(prev => [...prev, ...res.data.filter(n => !prev.some(p => p.id === n.id))]);
      setHasMore(res.data.length >= 100);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const fetchAnnouncement = async () => {
    try {
      const res = await api.get('/announcement?active=true');
      setAnnouncement(res.data && res.data.length > 0 ? res.data[0] : null);
    } catch { /* ignore */ }
  };

  const checkPushStatus = () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) { setPushStatus(null); return; }
    if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
    setPushStatus(localStorage.getItem('push_token') ? 'active' : 'available');
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    setPushError(null);
    try {
      const token = await requestPushPermission();
      if (token) {
        await api.post('/push/subscribe', { token });
        localStorage.setItem('push_token', token);
        setPushStatus('active');
      } else if (Notification.permission === 'denied') {
        setPushStatus('denied');
      }
    } catch (err) {
      console.error('[PUSH ERROR]', err);
      const msg = err?.code || err?.message || '';
      if (msg.includes('permission') || msg.includes('NotAllowed')) setPushStatus('denied');
      else setPushError('Aktivacija nije uspjela. Osvježi stranicu i pokušaj ponovo.');
    } finally {
      setPushLoading(false);
    }
  };

  const markRead = (ids) => {
    const set = new Set(ids);
    const next = notifications.map(n => (set.has(n.id) ? { ...n, is_read: true } : n));
    setNotifications(next);
    reportUnread(next);
    if (ids.length === 1) api.put(`/notifications/${ids[0]}/read`).catch(() => {});
    else api.post('/notifications/read', { ids }).catch(() => {});
  };

  const markUnread = (id) => {
    const next = notifications.map(n => (n.id === id ? { ...n, is_read: false } : n));
    setNotifications(next);
    reportUnread(next);
    api.put(`/notifications/${id}/unread`).catch(() => {});
  };

  const markAllRead = async () => {
    const next = notifications.map(n => ({ ...n, is_read: true }));
    setNotifications(next);
    onUnreadChange && onUnreadChange(0);
    api.put('/notifications/read-all').catch(() => {});
  };

  // Brisanje s „Poništi”: odmah sakrij, na backend tek kad istekne traka.
  const scheduleDelete = (ids, { readAll = false, label } = {}) => {
    commitPending();
    const nextHidden = new Set([...hidden, ...ids]);
    setHidden(nextHidden);
    reportUnread(notifications, nextHidden);
    const timer = setTimeout(() => {
      commitPending();
      setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
      setToast(t => (t && t.ids === ids ? null : t));
    }, UNDO_MS);
    pendingRef.current = { ids, readAll, timer };
    setToast({ ids, text: label || (ids.length === 1 ? 'Obavijest obrisana' : `Obrisano: ${ids.length}`) });
  };

  const undoDelete = () => {
    const p = pendingRef.current;
    if (p) { clearTimeout(p.timer); pendingRef.current = null; }
    const back = new Set(hidden);
    (toast?.ids || []).forEach(id => back.delete(id));
    setHidden(back);
    reportUnread(notifications, back);
    setToast(null);
  };

  const openTarget = async (n) => {
    if (!n.is_read) markRead([n.id]);
    const k = kindOf(n.title);
    // Intervencija (prijava, nova, dodijeljen): otvori baš tu intervenciju ako je poznata.
    if (n.int_id && onOpenIntervention) { onOpenIntervention(n.int_id); return; }
    if (k.target === 'interventions') { navigate && navigate('interventions'); return; }
    if (k.target === 'board') { onOpenBoard ? onOpenBoard() : navigate && navigate('home'); return; }
    if (isDhmz(n)) {
      // Aktivno upozorenje: Početna s otvorenim upozorenjem u Aktualno; isteklo: samo prikaži cijeli tekst.
      if (dhmzExpired(n)) { toggleExpanded(n.id); return; }
      onOpenDhmzWarning && onOpenDhmzWarning(n.ref_key);
      navigate && navigate('home');
      return;
    }
    if (n.task_id) {
      try {
        const res = await api.get(`/tasks/${n.task_id}`);
        setOpenTask({ task: res.data, participants: !!n.req_kind });
      } catch {
        setToast({ ids: null, text: 'Zadatak više ne postoji ili ti nije vidljiv.' });
        setTimeout(() => setToast(t => (t && t.ids === null ? null : t)), 3500);
      }
      return;
    }
    if (k.target === 'home') navigate && navigate('home');
  };

  const toggleSelect = (id) => setSelected(prev => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const exitSelect = () => { setSelecting(false); setSelected(new Set()); };

  if (loading) return <div className="loading">Učitavanje...</div>;

  const visible = notifications.filter(n => !hidden.has(n.id));
  const unreadCount = visible.filter(n => !n.is_read).length;
  const counts = { all: unreadCount };
  for (const n of visible) {
    const c = kindOf(n.title).cat;
    if (c === 'vol') continue;
    if (n.is_read) continue;
    counts[c] = (counts[c] || 0) + 1;
  }
  counts.vol = visible.filter(n => n.req_state === 'pending').length;
  const shown = filter === 'all' ? visible : visible.filter(n => kindOf(n.title).cat === filter);
  const readCount = visible.filter(n => n.is_read).length;

  // Grupiranje po danu; u „Prijave” neriješene idu na vrh u skupinu „Čeka odluku”.
  const days = [];
  const pendingFirst = filter === 'vol' ? shown.filter(n => n.req_state === 'pending') : [];
  if (pendingFirst.length) days.push({ key: 'Čeka odluku', items: pendingFirst, pending: true });
  for (const n of shown) {
    if (pendingFirst.includes(n)) continue;
    const key = dayKey(n.created_at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(n);
    else days.push({ key, items: [n] });
  }

  return (
    <div className="ntf-page" style={{ maxWidth: 640, margin: '0 auto', animation: 'fadeIn 0.2s ease-out' }}>

      {/* Zaglavlje — u odabiru pokazuje akcije nad odabranima */}
      {selecting ? (
        <div className="ntf-head ntf-head-select">
          <button type="button" className="ntf-hbtn" onClick={exitSelect} aria-label="Odustani"><X size={18} /></button>
          <span className="ntf-sel-count"><b>{selected.size}</b><span className="ntf-hide-xs"> odabrano</span></span>
          <button type="button" className="ntf-hbtn" disabled={!selected.size} onClick={() => { markRead([...selected]); exitSelect(); }}>
            <Check size={16} /> Pročitano
          </button>
          <button type="button" className="ntf-hbtn ntf-hbtn-red" disabled={!selected.size} onClick={() => { scheduleDelete([...selected]); exitSelect(); }}>
            <Trash2 size={16} /> Obriši
          </button>
        </div>
      ) : (
        <div className="ntf-head">
          <h2>Obavijesti</h2>
          {unreadCount > 0 && <span className="ntf-count tab">{unreadCount}</span>}
          <div className="ntf-head-actions">
            {box === 'in' && unreadCount > 0 && (
              <button type="button" className="ntf-hbtn" onClick={markAllRead} title="Označi sve pročitanim">
                <CheckCheck size={16} /> <span className="ntf-hide-xs">Sve pročitano</span>
              </button>
            )}
            {box === 'in' && readCount > 0 && (
              <button type="button" className={`ntf-hbtn${confirmClear ? ' is-on' : ''}`} onClick={() => setConfirmClear(v => !v)} title="Obriši sve pročitane">
                <Trash2 size={16} /> <span className="ntf-hide-xs">Očisti pročitane</span>
              </button>
            )}
          </div>
        </div>
      )}

      {canSend && !selecting && (
        <div style={{ marginBottom: 12 }}>
          <Seg value={box} onChange={(v) => { setBox(v); setConfirmClear(false); }}
            options={[{ value: 'in', label: 'Primljene' }, { value: 'out', label: 'Poslane' }]} />
        </div>
      )}

      {box === 'out' ? (
        <SentNotifications
          user={user}
          onOpenBoard={onOpenBoard}
          onOpenTask={async (taskId) => {
            try {
              const res = await api.get(`/tasks/${taskId}`);
              setOpenTask({ task: res.data, participants: false });
            } catch {
              setToast({ ids: null, text: 'Zadatak više ne postoji ili ti nije vidljiv.' });
              setTimeout(() => setToast(t => (t && t.ids === null ? null : t)), 3500);
            }
          }}
        />
      ) : <>
      {/* Potvrda „Očisti pročitane” — vlastita traka ispod zaglavlja (na mobitelu ne stane u zaglavlje) */}
      {!selecting && confirmClear && readCount > 0 && (
        <div className="ntf-confirm" role="alertdialog">
          <span>Obrisati {readCount} {readCount === 1 ? 'pročitanu obavijest' : readCount < 5 ? 'pročitane obavijesti' : 'pročitanih obavijesti'}?</span>
          <div className="ntf-confirm-btns">
            <button type="button" className="ntf-hbtn" onClick={() => setConfirmClear(false)}>Odustani</button>
            <button type="button" className="ntf-hbtn ntf-hbtn-solid-red" onClick={() => {
              setConfirmClear(false);
              scheduleDelete(visible.filter(n => n.is_read).map(n => n.id), { readAll: true, label: 'Pročitane obavijesti obrisane' });
            }}><Trash2 size={16} /> Obriši</button>
          </div>
        </div>
      )}

      {/* Tanki redci: istaknuta obavijest, push (DHMZ upozorenja su obične obavijesti) */}
      {(announcement || pushStatus === 'available' || pushStatus === 'denied') && (
        <div className="ntf-top">
          {announcement && (
            <button type="button" className="ntf-strip" style={{ borderLeftColor: '#1a2a4a' }} onClick={() => navigate && navigate('home')}>
              <Megaphone size={19} color="#1a2a4a" />
              <span className="ntf-strip-tx"><b className="ntf-clamp">{announcement.text}</b><small>Istaknuta obavijest</small></span>
              <ChevronRight size={17} color="#b2bec3" />
            </button>
          )}
          {pushStatus === 'available' && (
            <div className="ntf-strip" style={{ borderLeftColor: '#3a6ea3', cursor: 'default' }}>
              <Bell size={19} color="#3a6ea3" />
              <span className="ntf-strip-tx"><b>Uključi push obavijesti</b><small>Stižu i kad aplikacija nije otvorena</small></span>
              <button type="button" className="ntf-push-btn" onClick={handleEnablePush} disabled={pushLoading}>{pushLoading ? '…' : 'Uključi'}</button>
            </div>
          )}
          {pushStatus === 'denied' && (
            <div className="ntf-strip" style={{ borderLeftColor: '#c3c9d3', cursor: 'default' }}>
              <BellOff size={19} color="#8a94a6" />
              <span className="ntf-strip-tx"><b>Push obavijesti su blokirane</b><small>Uključi ih u postavkama uređaja ili preglednika</small></span>
            </div>
          )}
          {pushError && <div className="ntf-err">{pushError}</div>}
        </div>
      )}

      {/* Filteri */}
      <div className="ntf-chips">
        {FILTERS.map(f => (
          <button type="button" key={f.key} className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>
            {f.label}{counts[f.key] ? <span className="c tab">{counts[f.key]}</span> : null}
          </button>
        ))}
      </div>

      {/* Popis */}
      {shown.length === 0 ? (
        <div className="ntf-empty">
          <Bell size={34} color="#dfe6e9" />
          <div>{filter === 'all' ? 'Nema obavijesti.' : 'Nema obavijesti u ovoj skupini.'}</div>
        </div>
      ) : days.map(day => (
        <div key={day.key}>
          <div className={`ntf-day${day.pending ? ' ntf-day-pending' : ''}`}>{day.key}</div>
          <div className="ntf-card">
            {day.items.map(n => {
              const k = kindOf(n.title);
              const { kind, main } = splitTitle(n.title);
              const isSel = selected.has(n.id);
              return (
                <SwipeRow
                  key={n.id}
                  disabled={selecting}
                  isRead={n.is_read}
                  onTap={() => {
                    if (selecting) { toggleSelect(n.id); return; }
                    // Obavijest bez cilja (npr. lozinka): dodir samo proširi tekst.
                    if (!n.task_id && !k.target && !n.int_id) { if (!n.is_read) markRead([n.id]); toggleExpanded(n.id); return; }
                    openTarget(n);
                  }}
                  onSwipeRight={() => scheduleDelete([n.id])}
                  onSwipeLeft={() => (n.is_read ? markUnread(n.id) : markRead([n.id]))}
                  onLongPress={selecting ? null : () => { setSelecting(true); setSelected(new Set([n.id])); }}
                >
                  <div className={`ntf-row${n.is_read ? ' is-read' : ''}${isSel ? ' is-sel' : ''}${n.req_state && n.req_state !== 'pending' ? ' is-resolved' : ''}`}>
                    {selecting
                      ? <span className={`ntf-cb${isSel ? ' on' : ''}`}>{isSel && <Check size={14} strokeWidth={3} />}</span>
                      : n.req_state === 'pending'
                        ? <span className="ntf-dot ntf-dot-pending" title="Čeka odluku" />
                        : <span className="ntf-dot" style={{ visibility: n.is_read ? 'hidden' : 'visible' }} />}
                    <span className="ntf-ic" style={{ color: dhmzExpired(n) ? '#b2bec3' : isDhmz(n) ? dhmzHue(n) : k.hue }}><k.Icon size={20} /></span>
                    <NotifText
                      main={main}
                      sub={[kind, n.message].filter(Boolean).join(' · ')}
                      expanded={expanded.has(n.id)}
                      onToggle={() => toggleExpanded(n.id)}
                    />
                    <span className="ntf-side">
                      <span className="ntf-tm tab">{formatTime(n.created_at)}</span>
                      {dhmzExpired(n) && <span className="ntf-state" style={{ color: '#8a94a6', borderColor: '#c7ccd466' }}>isteklo</span>}
                      {n.req_state && REQ_STATE[n.req_state] && (
                        <span className="ntf-state" style={{ color: REQ_STATE[n.req_state].color, borderColor: `${REQ_STATE[n.req_state].color}66`, background: n.req_state === 'pending' ? '#fbf6ea' : 'transparent' }}>
                          {reqStateText(n)}
                        </span>
                      )}
                    </span>
                  </div>
                </SwipeRow>
              );
            })}
          </div>
        </div>
      ))}

      {hasMore && filter === 'all' && (
        <button type="button" className="ntf-more" onClick={loadOlder} disabled={loadingMore}>
          {loadingMore ? 'Učitavanje…' : 'Učitaj starije'}
        </button>
      )}

      {!selecting && visible.length > 0 && (
        <div className="ntf-hint">Povuci udesno za brisanje, ulijevo za „pročitano” / „nepročitano”. Dugi pritisak za odabir više obavijesti.</div>
      )}

      </>}

      {toast && (
        <div className="ntf-toast">
          <span>{toast.text}</span>
          {toast.ids && <button type="button" onClick={undoDelete}>Poništi</button>}
        </div>
      )}

      {openTask && (
        <TaskDetailModal
          key={openTask.task.id}
          task={openTask.task}
          startParticipantsOpen={openTask.participants}
          user={user}
          onClose={() => { setOpenTask(null); fetchNotifications(); }}
          onUpdate={(shouldClose) => { if (shouldClose) setOpenTask(null); }}
        />
      )}
    </div>
  );
}

export default Notifications;
